import {
  freezeCapturedNoteInitialQuestions,
  getNoteCacheKey,
  hasNoteStorage,
  markCapturedNoteSignedOff,
  saveCapturedNote
} from './NoteStore.ts';
import { flushDueEntries } from './NoteOffload.ts';
import {
  readFocusCaptureNote,
  type FocusCaptureSource
} from './NoteFocusCapture.ts';

const POLLING_INTERVAL_MS = 2500;
const RECENT_ACTIVITY_MS = 60 * 1000;

type CaptureInjector = {
  getPatientInfoForCapture: () => any;
  getPageInfoForCapture: () => any;
  getNoteKeyForCapture: () => string | null | Promise<string | null>;
  // Values keyed by question key; Flex questions may contain nested value maps.
  getQuestionsForCapture: () => | Record<string, any> | Promise<Record<string, any>>;
  // Identifies the structure this snapshot is in, for injectors that can scrape
  // the same note into more than one. See the merge in NoteStore.
  getQuestionShapeForCapture?: () => string | null;
  // How many levels NoteStore merges snapshots by (see saveCapturedNote).
  noteMergeDepthForCapture?: number;
  noteFinalizationSelectorForCapture?: string;
  // Event-driven injectors (NoteFocusCapture.ts) have this instead of the
  // *ForCapture functions, and are never polled.
  noteFocusCapture?: FocusCaptureSource;
};

type SaveOptions = {
  signedOffAt?: number;
  captureShape?: string;
  mergeDepth?: number;
};

let started = false;
let lastActivity = 0;
let captureRequested = false;
let noteCaptureEnabled = false;

export function setNoteCaptureEnabled(enabled: boolean) {
  noteCaptureEnabled = enabled;
}

export function isNoteCaptureEnabled() {
  return noteCaptureEnabled;
}

function canCaptureNotes() {
  return noteCaptureEnabled && hasNoteStorage();
}

function flushCapturedNotes(keepalive = false, forceCacheKey?: string) {
  if (!canCaptureNotes()) return;
  return forceCacheKey
    ? flushDueEntries(keepalive, forceCacheKey)
    : flushDueEntries(keepalive);
}

export function initNoteCapture(
  getInjector: () => CaptureInjector | null,
  emrKey: string | null
) {
  if (
    started ||
    !canCaptureNotes() ||
    !emrKey ||
    !supportsNoteCapture(getInjector())
  ) {
    return;
  }

  started = true;
  if (document.visibilityState === 'visible') {
    markActivity();
  }

  ['input', 'keydown'].forEach((eventName) => {
    document.addEventListener(eventName, markActivity, true);
  });
  document.addEventListener('click', (event) => {
    markActivity();
    const injector = getInjector();
    const selector = injector?.noteFinalizationSelectorForCapture;
    if (
      !selector ||
      !(event.target instanceof Element) ||
      !event.target.closest(selector)
    ) {
      return;
    }

    void finalizeCurrentNote(injector, getInjector, emrKey);
  }, true);

  captureCurrentNote(getInjector, emrKey).then(() => flushCapturedNotes());

  let tickInFlight = false;
  setInterval(async () => {
    if (!canCaptureNotes()) return;
    // Skip while the previous tick is still running so slow ticks can't pile up.
    if (tickInFlight) return;
    tickInFlight = true;
    try {
      if (shouldPollNow()) {
        captureRequested = false;
        await captureCurrentNote(getInjector, emrKey);
      }
      await flushCapturedNotes();
    } finally {
      tickInFlight = false;
    }
  }, POLLING_INTERVAL_MS);

  document.addEventListener('visibilitychange', () => {
    if (canCaptureNotes() && document.visibilityState === 'hidden') {
      captureCurrentNote(getInjector, emrKey);
    }
  });

  window.addEventListener('pagehide', () => {
    if (!canCaptureNotes()) return;
    void flushCapturedNotes(true);
    captureCurrentNote(getInjector, emrKey).then(() => flushCapturedNotes(true));
  });
}

async function captureCurrentNote(
  getInjector: () => CaptureInjector | null,
  emrKey: string,
  signedOffAt?: number,
  expectedNoteKey?: string
) {
  try {
    if (!canCaptureNotes()) return;
    const note = await readCurrentNote(getInjector);
    if (!note || !canCaptureNotes()) return;
    if (expectedNoteKey && note.noteKey !== expectedNoteKey) return;
    const options: SaveOptions = {};
    if (signedOffAt !== undefined) options.signedOffAt = signedOffAt;
    if (note.captureShape) options.captureShape = note.captureShape;
    if (note.mergeDepth) options.mergeDepth = note.mergeDepth;
    await saveCapturedNote(
      emrKey,
      note.patientInfo,
      note.pageInfo,
      note.noteKey,
      note.questions,
      options
    );
  } catch (_) {
    return;
  }
}

async function finalizeCurrentNote(
  injector: CaptureInjector,
  getInjector: () => CaptureInjector | null,
  emrKey: string
) {
  if (!canCaptureNotes()) return;
  const signedOffAt = Date.now();
  const noteKey = await Promise.resolve(injector.getNoteKeyForCapture());
  if (!noteKey) return;
  const signedNote = await markCapturedNoteSignedOff(
    emrKey,
    noteKey,
    signedOffAt
  );
  if (signedNote) {
    await flushCapturedNotes(true);
  }

  await captureCurrentNote(getInjector, emrKey, signedOffAt, noteKey);
  await flushCapturedNotes(true);
}

export async function linkNoteToConversation(
  getInjector: () => CaptureInjector | null,
  emrKey: string,
  conversationId: number,
  configId: number,
  generatedAnswers: Record<string, any> | undefined,
  expectedNoteKey: string
) {
  try {
    if (!canCaptureNotes()) return false;
    const injector = getInjector();
    const focusCapture = injector?.noteFocusCapture;
    const pollingCaptureSupported = supportsNoteCapture(injector);
    const capturedNote = pollingCaptureSupported
      ? await readCurrentNote(getInjector)
      : null;
    const focusNote = !pollingCaptureSupported && focusCapture
      ? await readFocusCaptureNote(focusCapture, emrKey)
      : null;
    const note = capturedNote ?? focusNote;
    if (
      !canCaptureNotes() ||
      !note ||
      note.noteKey !== expectedNoteKey
    ) {
      return false;
    }

    // Generated answers identify the fields targeted by this AI write.
    const savedNote = await saveCapturedNote(
      emrKey,
      note.patientInfo,
      note.pageInfo,
      note.noteKey,
      note.questions,
      {
        conversationId,
        configId,
        generatedAnswers,
        ...(capturedNote?.captureShape
          ? { captureShape: capturedNote.captureShape }
          : {}),
        ...(capturedNote?.mergeDepth
          ? { mergeDepth: capturedNote.mergeDepth }
          : {})
      }
    );
    await flushCapturedNotes(true, getNoteCacheKey(emrKey, note.noteKey));
    return savedNote.conversationId === conversationId;
  } catch (_) {
    return false;
  }
}

export async function freezeInitialNote(
  emrKey: string,
  expectedNoteKey: string
) {
  try {
    if (!canCaptureNotes()) return false;
    const savedNote = await freezeCapturedNoteInitialQuestions(
      emrKey,
      expectedNoteKey
    );
    return savedNote?.initialQuestionsFrozen === true;
  } catch (_) {
    return false;
  }
}

export async function captureNoteIdentity(
  getInjector: () => CaptureInjector | null
) {
  try {
    const injector = getInjector();
    const focusCapture = injector?.noteFocusCapture;
    let noteKey: string | null = null;
    if (supportsNoteCapture(injector)) {
      noteKey = await Promise.resolve(injector.getNoteKeyForCapture());
    } else if (focusCapture) {
      noteKey = focusCapture.getNoteKey();
    }
    // Old and new embed host/evaluator files can briefly mix during rollout.
    return noteKey ? { noteKey, pageUrl: document.URL } : null;
  } catch (_) {
    return null;
  }
}

async function readCurrentNote(getInjector: () => CaptureInjector | null) {
  const injector = getInjector();
  if (!injector?.getQuestionsForCapture) return null;
  const identity = await readNoteIdentity(injector);
  if (!identity) return null;
  const questions = await Promise.resolve(injector.getQuestionsForCapture());
  // Read synchronously alongside the questions it describes, so a change of
  // structure can never be attributed to the wrong snapshot.
  const captureShape = injector.getQuestionShapeForCapture?.() ?? undefined;
  const currentNoteKey = await Promise.resolve(
    injector.getNoteKeyForCapture()
  );
  if (currentNoteKey !== identity.noteKey) return null;
  if (Object.keys(questions).length === 0) return null;
  const mergeDepth = injector.noteMergeDepthForCapture;

  return { ...identity, questions, captureShape, mergeDepth };
}

async function readNoteIdentity(injector: CaptureInjector | null) {
  if (!supportsNoteCapture(injector)) {
    return null;
  }

  const noteKey = await Promise.resolve(injector.getNoteKeyForCapture());
  if (!noteKey) return null;
  const pageInfo = await Promise.resolve(injector.getPageInfoForCapture());
  const patientInfo = await Promise.resolve(
    injector.getPatientInfoForCapture()
  );
  return { patientInfo, pageInfo, noteKey };
}

function supportsNoteCapture(
  injector: CaptureInjector | null
): injector is CaptureInjector {
  return Boolean(
    injector?.getPatientInfoForCapture &&
    injector?.getPageInfoForCapture &&
    injector?.getNoteKeyForCapture &&
    injector?.getQuestionsForCapture
  );
}

function shouldPollNow() {
  return captureRequested || Date.now() - lastActivity <= RECENT_ACTIVITY_MS;
}

function markActivity() {
  lastActivity = Date.now();
  captureRequested = true;
}
